import { useState, useEffect, useRef, useCallback } from 'react'
import {
  Search,
  FileText,
  Download,
  Eye,
  Trash2,
  Plus,
  Upload,
  FolderOpen,
  Clock,
  CheckCircle,
  Loader2,
  X,
  FileUp,
  Type,
  AlertCircle,
  Copy,
} from 'lucide-react'
import { taskPipelineService } from '../../services/taskPipeline.service'
import { agentsService } from '../../services/agents.service'
import { TaskReferenceTemplate, CustomerTaskProgress, Agent } from '@/types'

type ActiveTab = 'templates' | 'documents'
type CreateMode = 'paste' | 'upload'

// ============= Status Badge Component =============

function StatusBadge({ status }: { status: string }) {
  const config: Record<string, { bg: string; text: string; icon: React.ReactNode }> = {
    completed: { bg: 'bg-green-50', text: 'text-green-700', icon: <CheckCircle className="w-3 h-3" /> },
    in_progress: { bg: 'bg-blue-50', text: 'text-blue-700', icon: <Loader2 className="w-3 h-3 animate-spin" /> },
    pending: { bg: 'bg-yellow-50', text: 'text-yellow-700', icon: <Clock className="w-3 h-3" /> },
    blocked: { bg: 'bg-gray-100', text: 'text-gray-600', icon: <AlertCircle className="w-3 h-3" /> },
    skipped: { bg: 'bg-gray-50', text: 'text-gray-500', icon: null },
  }
  const c = config[status] || config.pending
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${c.bg} ${c.text}`}>
      {c.icon}
      {status.replace('_', ' ')}
    </span>
  )
}

// ============= Main Component =============

export function DocumentsPage() {
  const [activeTab, setActiveTab] = useState<ActiveTab>('templates')

  // ── Reference Templates state ──
  const [templates, setTemplates] = useState<TaskReferenceTemplate[]>([])
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false)
  const [templateSearch, setTemplateSearch] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState<TaskReferenceTemplate | null>(null)

  // ── Generated Documents state ──
  const [documents, setDocuments] = useState<CustomerTaskProgress[]>([])
  const [documentsTotal, setDocumentsTotal] = useState(0)
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(false)
  const [docSearch, setDocSearch] = useState('')
  const [docStatusFilter, setDocStatusFilter] = useState('')
  const [docAgentFilter, setDocAgentFilter] = useState('')
  const [docPage, setDocPage] = useState(0)
  const [agents, setAgents] = useState<Agent[]>([])
  const [showDocViewModal, setShowDocViewModal] = useState<CustomerTaskProgress | null>(null)

  const [error, setError] = useState<string | null>(null)
  const DOC_PAGE_SIZE = 25

  // ── Load data ──

  const fetchTemplates = useCallback(async () => {
    setIsLoadingTemplates(true)
    setError(null)
    try {
      const data = await taskPipelineService.getReferenceTemplates()
      setTemplates(data)
    } catch (err) {
      setError('Failed to load reference templates')
      console.error(err)
    } finally {
      setIsLoadingTemplates(false)
    }
  }, [])

  const fetchDocuments = useCallback(async () => {
    setIsLoadingDocuments(true)
    setError(null)
    try {
      const result = await taskPipelineService.getGeneratedDocuments({
        search: docSearch || undefined,
        status: docStatusFilter || undefined,
        agent_id: docAgentFilter || undefined,
        limit: DOC_PAGE_SIZE,
        offset: docPage * DOC_PAGE_SIZE,
      })
      setDocuments(result.documents)
      setDocumentsTotal(result.total)
    } catch (err) {
      setError('Failed to load generated documents')
      console.error(err)
    } finally {
      setIsLoadingDocuments(false)
    }
  }, [docSearch, docStatusFilter, docAgentFilter, docPage])

  useEffect(() => {
    fetchTemplates()
    agentsService.getAgents().then(setAgents).catch(() => {})
  }, [fetchTemplates])

  useEffect(() => {
    if (activeTab === 'documents') {
      fetchDocuments()
    }
  }, [activeTab, fetchDocuments])

  // ── Template actions ──

  const handleDeleteTemplate = async (id: string) => {
    if (!confirm('Are you sure you want to delete this reference template?')) return
    try {
      await taskPipelineService.deleteReferenceTemplate(id)
      setTemplates(prev => prev.filter(t => t.id !== id))
    } catch (err) {
      console.error(err)
      alert('Failed to delete template')
    }
  }

  const handleTemplateCreated = (template: TaskReferenceTemplate) => {
    setTemplates(prev => [template, ...prev])
    setShowCreateModal(false)
  }

  // ── Document actions ──

  const handleDownloadDoc = async (doc: CustomerTaskProgress, format: 'docx' | 'pdf') => {
    try {
      const blob = await taskPipelineService.downloadDocument(doc.id, format)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${(doc as any).task_name || 'document'}.${format === 'docx' ? 'docx' : 'html'}`
      a.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error(err)
      alert('Failed to download document')
    }
  }

  // ── Filtered templates ──

  const filteredTemplates = templates.filter(t => {
    if (!templateSearch) return true
    const q = templateSearch.toLowerCase()
    return t.name.toLowerCase().includes(q) || (t.description || '').toLowerCase().includes(q)
  })

  const totalDocPages = Math.ceil(documentsTotal / DOC_PAGE_SIZE)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Documents</h1>
          <p className="text-gray-500 mt-1">
            Manage reference templates and view AI-generated documents
          </p>
        </div>
        {activeTab === 'templates' && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-500 text-white font-semibold rounded-xl hover:bg-emerald-600 transition-colors shadow-[3px_3px_0px_#1a1a2e]"
          >
            <Plus className="w-4 h-4" />
            Add Template
          </button>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border-2 border-[#1a1a2e] rounded-xl p-4 text-red-700 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-4 border-b border-[#1a1a2e]">
        <button
          onClick={() => setActiveTab('templates')}
          className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'templates'
              ? 'border-emerald-600 text-emerald-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Document Templates
          {templates.length > 0 && (
            <span className="ml-2 px-1.5 py-0.5 text-[10px] bg-gray-100 text-gray-600 rounded-full">{templates.length}</span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('documents')}
          className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'documents'
              ? 'border-emerald-600 text-emerald-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Generated Documents
          {documentsTotal > 0 && (
            <span className="ml-2 px-1.5 py-0.5 text-[10px] bg-gray-100 text-gray-600 rounded-full">{documentsTotal}</span>
          )}
        </button>
      </div>

      {/* ═══════════════ TAB 1: Reference Templates ═══════════════ */}
      {activeTab === 'templates' && (
        <>
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search templates..."
              value={templateSearch}
              onChange={(e) => setTemplateSearch(e.target.value)}
              className="w-full h-12 pl-12 pr-4 bg-white border-2 border-[#1a1a2e] rounded-xl text-sm text-gray-900 focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e]"
            />
          </div>

          {isLoadingTemplates ? (
            <div className="bg-white rounded-xl border-2 border-[#1a1a2e] p-12 flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
            </div>
          ) : filteredTemplates.length === 0 ? (
            <div className="bg-white rounded-xl border-2 border-[#1a1a2e] p-12">
              <div className="text-center max-w-md mx-auto">
                <div className="w-16 h-16 rounded-xl bg-gray-50 flex items-center justify-center mx-auto mb-6">
                  <FolderOpen className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {templateSearch ? 'No templates found' : 'No reference templates yet'}
                </h3>
                <p className="text-gray-500 mb-6">
                  {templateSearch
                    ? 'Try adjusting your search'
                    : 'Upload or paste reference documents that your AI agents will use as structural guides when generating documents.'}
                </p>
                {!templateSearch && (
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-500 text-white font-semibold rounded-xl hover:bg-emerald-600 transition-colors shadow-[3px_3px_0px_#1a1a2e]"
                  >
                    <Plus className="w-4 h-4" />
                    Add Template
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl border-2 border-[#1a1a2e] overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Name</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">Source</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden sm:table-cell">Created</th>
                    <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredTemplates.map(template => (
                    <tr key={template.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-emerald-50 rounded-lg flex-shrink-0">
                            <FileText className="w-5 h-5 text-emerald-600" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-gray-900 truncate">{template.name}</p>
                            {template.description && (
                              <p className="text-sm text-gray-500 truncate max-w-xs">{template.description}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 hidden md:table-cell">
                        <span className="text-sm text-gray-600">
                          {template.original_filename ? (
                            <span className="inline-flex items-center gap-1">
                              <FileUp className="w-3.5 h-3.5" />
                              {template.original_filename}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1">
                              <Type className="w-3.5 h-3.5" />
                              Pasted text
                            </span>
                          )}
                        </span>
                      </td>
                      <td className="px-6 py-4 hidden sm:table-cell">
                        <span className="text-sm text-gray-500">
                          {new Date(template.created_at).toLocaleDateString()}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => setShowViewModal(template)}
                            className="p-2 hover:bg-emerald-50 rounded-lg transition-colors"
                            title="View content"
                          >
                            <Eye className="w-4 h-4 text-gray-500 hover:text-emerald-600" />
                          </button>
                          <button
                            onClick={() => handleDeleteTemplate(template.id)}
                            className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ═══════════════ TAB 2: Generated Documents ═══════════════ */}
      {activeTab === 'documents' && (
        <>
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by document or customer name..."
                value={docSearch}
                onChange={(e) => { setDocSearch(e.target.value); setDocPage(0) }}
                className="w-full h-11 pl-12 pr-4 bg-white border-2 border-[#1a1a2e] rounded-xl text-sm text-gray-900 focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e]"
              />
            </div>
            <select
              value={docStatusFilter}
              onChange={(e) => { setDocStatusFilter(e.target.value); setDocPage(0) }}
              className="h-11 px-4 bg-white border-2 border-[#1a1a2e] rounded-xl text-sm text-gray-900"
            >
              <option value="">All Statuses</option>
              <option value="completed">Completed</option>
              <option value="in_progress">In Progress</option>
              <option value="pending">Pending</option>
              <option value="blocked">Blocked</option>
            </select>
            {agents.length > 0 && (
              <select
                value={docAgentFilter}
                onChange={(e) => { setDocAgentFilter(e.target.value); setDocPage(0) }}
                className="h-11 px-4 bg-white border-2 border-[#1a1a2e] rounded-xl text-sm text-gray-900"
              >
                <option value="">All Agents</option>
                {agents.map(a => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
            )}
          </div>

          {isLoadingDocuments ? (
            <div className="bg-white rounded-xl border-2 border-[#1a1a2e] p-12 flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
            </div>
          ) : documents.length === 0 ? (
            <div className="bg-white rounded-xl border-2 border-[#1a1a2e] p-12">
              <div className="text-center max-w-md mx-auto">
                <div className="w-16 h-16 rounded-xl bg-gray-50 flex items-center justify-center mx-auto mb-6">
                  <FileText className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {docSearch || docStatusFilter || docAgentFilter ? 'No documents found' : 'No generated documents yet'}
                </h3>
                <p className="text-gray-500">
                  {docSearch || docStatusFilter || docAgentFilter
                    ? 'Try adjusting your search or filters'
                    : 'Documents will appear here when your AI agents generate them through task pipelines.'}
                </p>
              </div>
            </div>
          ) : (
            <>
              <div className="bg-white rounded-xl border-2 border-[#1a1a2e] overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Document</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">Customer</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden lg:table-cell">Status</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden sm:table-cell">Rev</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden sm:table-cell">Updated</th>
                      <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {documents.map(doc => (
                      <tr key={doc.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-emerald-50 rounded-lg flex-shrink-0">
                              <FileText className="w-5 h-5 text-emerald-600" />
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-gray-900 truncate">{(doc as any).task_name || 'Untitled Document'}</p>
                              {(doc as any).task_description && (
                                <p className="text-sm text-gray-500 truncate max-w-xs">{(doc as any).task_description}</p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 hidden md:table-cell">
                          <span className="text-sm text-gray-700">{(doc as any).customer_name || 'Unknown'}</span>
                        </td>
                        <td className="px-6 py-4 hidden lg:table-cell">
                          <StatusBadge status={doc.status} />
                        </td>
                        <td className="px-6 py-4 hidden sm:table-cell">
                          <span className="text-sm text-gray-600">v{doc.revision_number}</span>
                        </td>
                        <td className="px-6 py-4 hidden sm:table-cell">
                          <span className="text-sm text-gray-500">
                            {new Date(doc.updated_at).toLocaleDateString()}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-end gap-1">
                            {doc.generated_content && (
                              <>
                                <button
                                  onClick={() => setShowDocViewModal(doc)}
                                  className="p-2 hover:bg-emerald-50 rounded-lg transition-colors"
                                  title="View content"
                                >
                                  <Eye className="w-4 h-4 text-gray-500 hover:text-emerald-600" />
                                </button>
                                <button
                                  onClick={() => handleDownloadDoc(doc, 'docx')}
                                  className="p-2 hover:bg-emerald-50 rounded-lg transition-colors"
                                  title="Download DOCX"
                                >
                                  <Download className="w-4 h-4 text-gray-500 hover:text-emerald-600" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalDocPages > 1 && (
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-500">
                    Showing {docPage * DOC_PAGE_SIZE + 1}–{Math.min((docPage + 1) * DOC_PAGE_SIZE, documentsTotal)} of {documentsTotal}
                  </p>
                  <div className="flex gap-2">
                    <button
                      disabled={docPage === 0}
                      onClick={() => setDocPage(p => p - 1)}
                      className="px-3 py-1.5 text-sm font-medium rounded-lg border-2 border-[#1a1a2e] text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <button
                      disabled={docPage >= totalDocPages - 1}
                      onClick={() => setDocPage(p => p + 1)}
                      className="px-3 py-1.5 text-sm font-medium rounded-lg border-2 border-[#1a1a2e] text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* ═══════════════ Create Template Modal ═══════════════ */}
      {showCreateModal && (
        <CreateTemplateModal
          onClose={() => setShowCreateModal(false)}
          onCreated={handleTemplateCreated}
        />
      )}

      {/* ═══════════════ View Template Modal ═══════════════ */}
      {showViewModal && (
        <ViewContentModal
          title={showViewModal.name}
          content={showViewModal.content}
          onClose={() => setShowViewModal(null)}
        />
      )}

      {/* ═══════════════ View Document Modal ═══════════════ */}
      {showDocViewModal && (
        <ViewContentModal
          title={(showDocViewModal as any).task_name || 'Generated Document'}
          content={showDocViewModal.generated_content || 'No content available.'}
          onClose={() => setShowDocViewModal(null)}
        />
      )}
    </div>
  )
}

// ═══════════════════════════════════════════════════════════
// Create Template Modal
// ═══════════════════════════════════════════════════════════

function CreateTemplateModal({
  onClose,
  onCreated,
}: {
  onClose: () => void
  onCreated: (template: TaskReferenceTemplate) => void
}) {
  const [mode, setMode] = useState<CreateMode>('paste')
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [content, setContent] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)

  const canSubmit = name.trim() && (mode === 'paste' ? content.trim() : file !== null)

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    if (f) {
      // Validate extension
      const ext = f.name.split('.').pop()?.toLowerCase()
      if (ext !== 'txt' && ext !== 'md') {
        setSubmitError('Only .txt and .md files are supported')
        return
      }
      setFile(f)
      setSubmitError('')
      if (!name.trim()) {
        setName(f.name.replace(/\.(txt|md)$/i, ''))
      }
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    const f = e.dataTransfer.files[0]
    if (f) {
      const ext = f.name.split('.').pop()?.toLowerCase()
      if (ext !== 'txt' && ext !== 'md') {
        setSubmitError('Only .txt and .md files are supported')
        return
      }
      setFile(f)
      setSubmitError('')
      if (!name.trim()) {
        setName(f.name.replace(/\.(txt|md)$/i, ''))
      }
    }
  }

  const handleSubmit = async () => {
    if (!canSubmit) return
    setIsSubmitting(true)
    setSubmitError('')

    try {
      let template: TaskReferenceTemplate
      if (mode === 'paste') {
        template = await taskPipelineService.createReferenceTemplate({
          name: name.trim(),
          description: description.trim() || undefined,
          content: content,
          file_type: 'text/plain',
        })
      } else {
        template = await taskPipelineService.uploadReferenceTemplate(
          file!,
          name.trim(),
          description.trim() || undefined,
        )
      }
      onCreated(template)
    } catch (err: any) {
      setSubmitError(err?.response?.data?.error || err?.message || 'Failed to create template')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl w-full max-w-lg max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#1a1a2e]">
          <h2 className="text-lg font-bold text-gray-900">Add Reference Template</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {/* Mode toggle */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setMode('paste')}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                mode === 'paste' ? 'bg-white text-gray-900 shadow-[3px_3px_0px_#1a1a2e]' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Type className="w-4 h-4" />
              Paste Text
            </button>
            <button
              onClick={() => setMode('upload')}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                mode === 'upload' ? 'bg-white text-gray-900 shadow-[3px_3px_0px_#1a1a2e]' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Upload className="w-4 h-4" />
              Upload File
            </button>
          </div>

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Template Name *</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g., Insurance Quote Template"
              className="w-full h-10 px-3 border-2 border-[#1a1a2e] rounded-lg text-sm text-gray-900 focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e]"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Description (optional)</label>
            <input
              type="text"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Brief description of what this template is for"
              className="w-full h-10 px-3 border-2 border-[#1a1a2e] rounded-lg text-sm text-gray-900 focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e]"
            />
          </div>

          {/* Content area */}
          {mode === 'paste' ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Template Content *</label>
              <textarea
                value={content}
                onChange={e => setContent(e.target.value)}
                placeholder="Paste the reference document content here..."
                rows={12}
                className="w-full px-3 py-2 border-2 border-[#1a1a2e] rounded-lg text-sm text-gray-900 focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e] font-mono resize-y"
              />
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Upload File *</label>
              <div
                onDragOver={e => e.preventDefault()}
                onDrop={handleDrop}
                className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-emerald-400 transition-colors cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
              >
                {file ? (
                  <div className="flex items-center justify-center gap-3">
                    <FileText className="w-8 h-8 text-emerald-600" />
                    <div className="text-left">
                      <p className="font-medium text-gray-900">{file.name}</p>
                      <p className="text-sm text-gray-500">{(file.size / 1024).toFixed(1)} KB</p>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); setFile(null) }}
                      className="p-1 hover:bg-gray-100 rounded"
                    >
                      <X className="w-4 h-4 text-gray-400" />
                    </button>
                  </div>
                ) : (
                  <>
                    <Upload className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600 font-medium">Drop your file here or click to browse</p>
                    <p className="text-sm text-gray-500 mt-1">Supports .txt and .md files only</p>
                  </>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  accept=".txt,.md"
                  onChange={handleFileChange}
                />
              </div>
            </div>
          )}

          {submitError && (
            <div className="bg-red-50 border-2 border-[#1a1a2e] rounded-lg p-3 text-sm text-red-700 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {submitError}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 px-6 py-4 border-t border-[#1a1a2e]">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit || isSubmitting}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-emerald-500 rounded-xl hover:bg-emerald-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
            {mode === 'paste' ? 'Create Template' : 'Upload Template'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════
// View Content Modal (shared for templates and generated docs)
// ═══════════════════════════════════════════════════════════

function ViewContentModal({
  title,
  content,
  onClose,
}: {
  title: string
  content: string
  onClose: () => void
}) {
  const handleCopy = () => {
    navigator.clipboard.writeText(content).catch(() => {})
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#1a1a2e]">
          <h2 className="text-lg font-bold text-gray-900 truncate">{title}</h2>
          <div className="flex items-center gap-1">
            <button onClick={handleCopy} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" title="Copy to clipboard">
              <Copy className="w-4 h-4 text-gray-400" />
            </button>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <pre className="whitespace-pre-wrap text-sm text-gray-800 font-mono leading-relaxed">{content}</pre>
        </div>
      </div>
    </div>
  )
}

export default DocumentsPage
